import { apiRequest } from '@/utils/api-request';

/**
 * Durable checkpoint statuses the live-transcription endpoint can report.
 *
 * `active` / `stopping` are the only live ones. `inactive` means the durable
 * checkpoint is gone (expired or never started); `failed` / `stopped` /
 * `cancelled` / `error` mirror the backend's terminal run statuses, which a
 * finalized checkpoint can carry.
 */
export type LiveTranscriptionStatus =
  | 'active'
  | 'stopping'
  | 'final'
  | 'inactive'
  | 'failed'
  | 'stopped'
  | 'cancelled'
  | 'error';

/**
 * Statuses after which no further transcript can ever arrive, so pollers must
 * stop. Anything not listed here is treated as still-running, which keeps an
 * unknown future status polling rather than silently going dark.
 */
const TERMINAL_LIVE_TRANSCRIPTION_STATUSES: ReadonlySet<string> = new Set([
  'final',
  'inactive',
  'failed',
  'stopped',
  'cancelled',
  'error',
]);

export function isTerminalLiveTranscriptionStatus(
  status: string | undefined,
): boolean {
  return (
    status !== undefined && TERMINAL_LIVE_TRANSCRIPTION_STATUSES.has(status)
  );
}

export interface LiveTranscriptionState {
  status: LiveTranscriptionStatus;
  finalized_transcript?: string;
  sequence?: number;
}

export interface StopLiveTranscriptionResult {
  status: LiveTranscriptionStatus;
}

const liveTranscriptionPath = (
  orgId: string,
  workflowId: string,
  sessionId: string,
  nodeId: string,
) =>
  `/workflow/${orgId}/${workflowId}/${sessionId}/nodes/${nodeId}/live-transcription`;

export async function getLiveTranscriptionState(
  orgId: string,
  workflowId: string,
  sessionId: string,
  nodeId: string,
): Promise<LiveTranscriptionState> {
  const response = await apiRequest<LiveTranscriptionState>({
    url: liveTranscriptionPath(orgId, workflowId, sessionId, nodeId),
    method: 'GET',
    target: 'workflow',
    suppressSentryStatuses: [404],
  });

  if (
    response.success === false ||
    !response.data?.status ||
    typeof response.data.status !== 'string'
  ) {
    throw new Error(response.detail || 'Live transcription state unavailable');
  }

  return response.data;
}

export async function stopLiveTranscription(
  orgId: string,
  workflowId: string,
  sessionId: string,
  nodeId: string,
) {
  const response = await apiRequest<StopLiveTranscriptionResult>({
    url: `${liveTranscriptionPath(orgId, workflowId, sessionId, nodeId)}/stop`,
    method: 'POST',
    target: 'workflow',
    body: {},
  });

  if (
    response.success === false ||
    !response.data?.status ||
    typeof response.data.status !== 'string'
  ) {
    throw new Error(response.detail || 'Failed to stop live transcription');
  }

  return response.data;
}
